import React from 'react'
import InstallPwa from '../components/InstallPwa.jsx';
import BankingDetailsForm from '../components/BankingDetailsForm.jsx';
import AccountDeletionModal from '../components/AccountDeletionModal.jsx';
export default function ProfilePage({ user, onUserUpdate, installPromptEvent, onInstallApp }) {
    const [profileData, setProfileData] = React.useState({
        id: user.id,
        name: user.name,
        email: user.email,
        roles: user.roles || [],
        activeRole: user.activeRole || '',
        phone_number: '',
        address: '',
        city: '',
        state: '',
        zip_code: '',
        id_number: ''
    });
    const [loading, setLoading] = React.useState(true);
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [successMessage, setSuccessMessage] = React.useState(null);
    const [isAddingRole, setIsAddingRole] = React.useState(false);
    const [addRoleError, setAddRoleError] = React.useState(null);
    const [addRoleSuccess, setAddRoleSuccess] = React.useState(null);
    // --- NEW: State for deletion modal ---
    const [showDeletionModal, setShowDeletionModal] = React.useState(false);
    
    // --- NEW: State for CIPC upload ---
    const [cipcFiles, setCipcFiles] = React.useState([]);
    const [isUploadingCipc, setIsUploadingCipc] = React.useState(false);
    const [cipcError, setCipcError] = React.useState(null);
    const [cipcSuccess, setCipcSuccess] = React.useState(null);
    const [cipcInputKey, setCipcInputKey] = React.useState(0); // To reset file input

    // Fetch user profile data on component mount
    React.useEffect(() => {
        const fetchProfile = async () => {
            try {
                setLoading(true);
                const response = await fetch(`/api/get_profile.php?user_id=${user.id}`);
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const data = await response.json();
                setProfileData(prevData => ({ ...prevData, ...data })); // Merge fetched data
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchProfile();
    }, [user.id]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setProfileData(prevData => ({
            ...prevData,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError(null);
        setSuccessMessage(null);

        try {
            const response = await fetch('/api/update_profile.php', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(profileData)
            });
            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.message || 'Failed to update profile.');
            }
            setSuccessMessage(result.message);
            // Update user in localStorage and App state if name/email changed
            if (onUserUpdate && (user.name !== profileData.name || user.email !== profileData.email)) {
                // Only pass back the fields that changed
                onUserUpdate({ name: profileData.name, email: profileData.email });
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleAddRole = async (roleToAdd) => {
        setIsAddingRole(true);
        setAddRoleError(null);
        setAddRoleSuccess(null);

        try {
            const response = await fetch('/api/add_role.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ user_id: user.id, new_role: roleToAdd })
            });
            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.message || 'Failed to add role.');
            }

            setAddRoleSuccess(result.message);
            
            // Update the user object in the parent component and local storage
            if (onUserUpdate) {
                const newRoles = [...user.roles, roleToAdd];
                onUserUpdate({ 
                    roles: newRoles, 
                    status: result.newStatus // Pass the new status up to app.js
                });
            }
        } catch (err) {
            setAddRoleError(err.message);
        } finally {
            setIsAddingRole(false);
        }
    };

    // --- NEW: Handler for deletion request ---
    const handleAccountDeletionRequest = async (reason) => {
        // This function is passed to the modal and returns a promise
        const response = await fetch('/api/request_account_deletion.php', {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ reason: reason })
        });

        const result = await response.json();
        if (!response.ok) {
            throw new Error(result.error || 'Failed to submit deletion request.');
        }
        return result; // Return the success message
    };

    // --- NEW: Handler for CIPC upload ---
    const handleCipcFileChange = (e) => {
        if (e.target.files.length > 0) {
            setCipcFiles(Array.from(e.target.files));
            setCipcError(null);
            setCipcSuccess(null);
        }
    };

    const handleCipcSubmit = async (e) => {
        e.preventDefault();
        if (cipcFiles.length === 0) {
            setCipcError("Please select documents to upload.");
            return;
        }
        setIsUploadingCipc(true);
        setCipcError(null);
        setCipcSuccess(null);

        const formData = new FormData();
        cipcFiles.forEach(file => {
            formData.append('documents[]', file);
        });

        try {
            const response = await fetch('/api/submit_suspension_documents.php', {
                method: 'POST',
                credentials: 'include',
                body: formData,
            });
            const result = await response.json();
            if (!response.ok) throw new Error(result.message || 'Upload failed.');

            setCipcSuccess("CIPC documents uploaded successfully.");
            setCipcFiles([]);
            setCipcInputKey(prev => prev + 1); // Reset file input
            
            // --- NEW: Update local user state to reflect pending review status ---
            if (onUserUpdate) {
                onUserUpdate({ status: 'pending_review' });
            }
        } catch (err) {
            setCipcError(err.message);
        } finally {
            setIsUploadingCipc(false);
        }
    };

    const canBecomeSeller = user && user.roles && !user.roles.includes('seller');
    const canBecomeBuyer = user && user.roles && !user.roles.includes('buyer');

    if (loading) {
        return (
            <div className="min-h-screen py-8 flex items-center justify-center">
                <i className="fas fa-spinner fa-spin text-6xl text-blue-400"></i>
            </div>
        );
    }

    return (
        <div data-name="profile-page" data-file="pages/ProfilePage.js" className="min-h-screen py-8">
            <div className="container mx-auto px-4 max-w-2xl">
                <div className="mb-8">
                    <InstallPwa installPromptEvent={installPromptEvent} onInstallApp={onInstallApp} />
                </div>

                <div className="bg-white rounded-xl shadow-lg p-8">
                    <h1 className="text-3xl font-bold text-gray-900 mb-6">My Profile</h1>

                    {error && <div className="bg-red-100 text-red-700 p-3 rounded-lg text-sm mb-4">{error}</div>}
                    {successMessage && <div className="bg-green-100 text-green-700 p-3 rounded-lg text-sm mb-4">{successMessage}</div>}

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
                            <input type="text" name="name" required value={profileData.name || ''} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">ID Number / Passport Number</label>
                            <input
                                type="text"
                                name="id_number"
                                value={profileData.id_number || ''}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                placeholder={user.activeRole === 'buyer' ? "Required to make payments" : "Required to receive payouts"}
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                {user.activeRole === 'buyer' ? "This is required to make secure payments via TradeSafe." : "This is required for sellers and agents to receive payouts."}
                            </p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                            <input type="email" name="email" required value={profileData.email || ''} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                            <input type="text" name="phone_number" value={profileData.phone_number || ''} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                            <input type="text" name="address" value={profileData.address || ''} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                                <input type="text" name="city" value={profileData.city || ''} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                                <input type="text" name="state" value={profileData.state || ''} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Zip Code</label>
                                <input type="text" name="zip_code" value={profileData.zip_code || ''} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Account Type</label>
                            <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100">
                                <span className="font-semibold capitalize">{profileData.activeRole}</span>
                                {profileData.roles && profileData.roles.length > 1 && (
                                    <span className="text-sm text-gray-500 ml-2">
                                        (Also: {profileData.roles.filter(r => r !== profileData.activeRole).map(r => r.charAt(0).toUpperCase() + r.slice(1)).join(', ')})
                                    </span>
                                )}
                            </div>
                        </div>
                        
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="w-full primary-gradient text-white py-3 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isSubmitting ? 'Saving...' : 'Save Profile'}
                        </button>
                    </form>
                </div>

                {/* Role Management Section */}
                <div className="bg-white rounded-xl shadow-lg p-8 mt-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6">Account Actions</h2>
                    {addRoleError && <div className="bg-red-100 text-red-700 p-3 rounded-lg text-sm mb-4">{addRoleError}</div>}
                    {addRoleSuccess && <div className="bg-green-100 text-green-700 p-3 rounded-lg text-sm mb-4">{addRoleSuccess}</div>}

                    {canBecomeSeller && (
                        <button onClick={() => handleAddRole('seller')} disabled={isAddingRole} className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50">
                            {isAddingRole ? 'Processing...' : 'Become a Seller'}
                        </button>
                    )}
                    {canBecomeBuyer && (
                        <button onClick={() => handleAddRole('buyer')} disabled={isAddingRole} className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 mt-2">
                            {isAddingRole ? 'Processing...' : 'Become a Buyer'}
                        </button>
                    )}
                    {!canBecomeSeller && !canBecomeBuyer && (
                        <p className="text-gray-500 text-sm">You have all available account roles.</p>
                    )}
                </div>

                {/* Banking Details Form for Sellers and Admins */}
                {(user.activeRole === 'seller' || user.activeRole === 'admin') && (
                    <BankingDetailsForm user={user} onUserUpdate={onUserUpdate} />
                )}

                {/* --- NEW: CIPC Documents Section for Sellers --- */}
                {user.roles && user.roles.includes('seller') && user.activeRole === 'seller' && (
                    <div className="bg-white rounded-xl shadow-lg p-8 mt-8">
                        <h2 className="text-2xl font-bold text-gray-900 mb-6">Business Verification Documents (CIPC)</h2>
                        <p className="text-sm text-gray-500 mb-4">
                            Please upload your latest CIPC registration documents here. These are stored securely and used for account verification.
                        </p>
                        
                        {cipcError && <div className="bg-red-100 text-red-700 p-3 rounded-lg text-sm mb-4">{cipcError}</div>}
                        {cipcSuccess && <div className="bg-green-100 text-green-700 p-3 rounded-lg text-sm mb-4">{cipcSuccess}</div>}

                        <form onSubmit={handleCipcSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Upload Documents</label>
                                <input 
                                    key={cipcInputKey}
                                    type="file" 
                                    multiple 
                                    onChange={handleCipcFileChange} 
                                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                                />
                                {cipcFiles.length > 0 && (
                                    <ul className="mt-2 text-sm text-gray-600 list-disc list-inside">
                                        {cipcFiles.map((f, index) => <li key={index}>{f.name}</li>)}
                                    </ul>
                                )}
                            </div>
                            <div className="flex justify-end">
                                <button
                                    type="submit"
                                    disabled={isUploadingCipc}
                                    className="primary-gradient text-white py-2 px-6 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
                                >
                                    {isUploadingCipc ? 'Uploading...' : 'Upload Documents'}
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                {/* --- NEW: Account Deletion Section --- */}
                <div className="bg-red-50 border border-red-200 rounded-xl p-6 mt-8">
                    <h2 className="text-xl font-bold text-red-800 mb-2">Delete Account</h2>
                    <p className="text-red-700 text-sm mb-4">
                        Request the permanent deletion of your account and all associated data. This action is irreversible and will be processed by an administrator.
                    </p>
                    <button
                        onClick={() => setShowDeletionModal(true)}
                        className="bg-red-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-red-700 transition-colors"
                    >
                        Request Account Deletion
                    </button>
                </div>
            </div>

            <AccountDeletionModal
                isOpen={showDeletionModal}
                onClose={() => setShowDeletionModal(false)}
                onConfirm={handleAccountDeletionRequest}
                user={user}
            />
        </div>
    );
}
